<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>

import { getDataUrl, getNeutralColors, isWhite } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import Dictionary from '../../dictionary';
import * as d3v4 from "d3v4";

  export default {
    name: 'SpatialCoverageChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      data: {
        type: Array,
        required: true,
      },
      language: {
        type: String,
        required: true
      },
      mode: {
        type: String,
        required: true
      },
      mediaMode:{
        type: Boolean,
        default: false
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      embed:{
        type: Boolean,
        default: false
      },
      league:{
        type: Object,
        required: false
      },
      year:{
        type: Number,
        required: false
      },
      mapTitle:{
        type: String,
        required: false
      },
      teamMap:{
        type:Boolean,
        default: false
      },
      team:{
        type: Object,
        required: false
      },
      seasons: {
        type: Array,
        required: false,
      },
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        if(this.margin){
          return this.SVG_WIDTH - this.margin.left - this.margin.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        return this.PITCH_WIDTH * 0.67125;
      },
      rootElementId(){
        return `pitchSpatialCoverage${this.id}`
      },
      currentUserLanguage() {
        return this.$store.getters["auth/language"];
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      averageData(){
        let avgData = _.find(this.data,(d) => d.teamId === 99);
        _.forEach(avgData,(v,f) => {
          if(v == null){
            avgData[f] = 0;
          };
        });
        return avgData;
      },
      averageDataBySeason(){
        let avgData = _.filter(this.data,(d) => d.teamId === 99);
        return _.reduce(avgData,(accum,o,i) => {
          accum[o.seasonId] = o;
          _.forEach(accum[o.seasonId],(v,f) => {
            if(v == null){
              accum[o.seasonId][f] = 0;
            };
          });
          return accum;
        },{});
      },
      seasonLabel(){
        return _.reduce(this.seasons,(accum,c,i) => {
          if(i != 0){
            accum = accum + ", ";
          }
          accum = accum + c.seasonName;
          return accum;
        },"")
      }
    },
    data() {
      return {
        images: {},
        observer: null,
        forceCollide: 4,
        sizeOffset: 0.75
      }
    },
    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.printD3();
    },
    methods: {
      async printD3(force = false){
        if(force){
          this.images = {};
        }

        const SVG_WIDTH = this.SVG_WIDTH;
        const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

        const PITCH_WIDTH = this.PITCH_WIDTH;
        const PITCH_HEIGHT = this.PITCH_HEIGHT;

        d3.selectAll('.tooltip-no-arrow').remove();

        d3.select(`#${this.rootElementId}`).select("svg").remove();

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow");
        const tooltipTop = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow")
          .attr("id","top");
        const tooltipBottom = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow")
          .attr("id","bottom");

        var svg = d3.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", SVG_WIDTH)
          .attr("height", PITCH_HEIGHT + margin.top + margin.bottom)
          .style("background-color", "#ffffff")
          .attr("id",`${this.rootElementId}SVG`)
          .attr("class", "pitch");

        this.drawImageBase64(svg, margin.left, margin.top, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`),"pitch",true);

        // Draw Driblab Logo
        this.drawImageBase64(svg,
          PITCH_WIDTH * 0.75 + margin.left ,PITCH_HEIGHT * 0.77 + margin.top,
        PITCH_WIDTH * 0.16,
        PITCH_HEIGHT * 0.18,
        require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`),'logoDriblab');

        //LeagueName || TeamName
        let leagueImgW = PITCH_WIDTH * 0.05;
        let leagueImgH = PITCH_HEIGHT * 0.05;
        let leagueImgX = PITCH_WIDTH * 0.07 + margin.left
        let leagueImgY = PITCH_HEIGHT * 0.02 + margin.top
        let leagueLink = this.teamMap ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + this.team.teamId + '.png' : 
        'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + this.league.id + '.png'
        this.drawImageBase64(svg,
           leagueImgX,leagueImgY,leagueImgW,leagueImgH,leagueLink
         ,'leagueCrest');

        let leagueTextSize = PITCH_WIDTH * 0.02; //18
        let leagueName = this.teamMap ? this.team.teamName : this.league.label
        let leagueYear = this.teamMap ? this.seasonLabel : this.year
        let logoSpacing = PITCH_WIDTH * 0.007; //8
        let leagueTextColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let leagueTextEl = this.writeText(svg, leagueImgX + leagueImgW + logoSpacing , leagueImgY + (leagueImgH/2) - (leagueTextSize / 3) , "start",
        leagueTextSize, `${leagueName}`,leagueTextColor)

        if(!this.teamMap || _.size(this.seasons) === 1){
          let leagueYearTextSize = this.teamMap && _.size(this.seasons) > 1 ? leagueTextSize / (this.seasons.length * 0.66) : leagueTextSize;
          this.writeText(svg, leagueImgX + leagueImgW + (logoSpacing * 2) + leagueTextEl[0][0].getBoundingClientRect().width , leagueImgY + (leagueImgH/2) - (leagueYearTextSize / 3.5) , "start",
          leagueYearTextSize, ` · ${leagueYear}`,leagueTextColor)
        }
        

        //Map Title
        this.writeText(svg, PITCH_WIDTH * 0.915 + margin.left , leagueImgY + (leagueImgH/2) - (leagueTextSize / 3) , "end",
        leagueTextSize, this.mapTitle,leagueTextColor) 

        //Arrow Attack Direction
        let arrowWidth = this.SVG_WIDTH * 0.2;
        let arrowHeight = arrowWidth * 0.3;
        let arrowX = this.SVG_WIDTH * 0.085;
        let arrowY = this.PITCH_HEIGHT * 0.915;
        let arrowImg;
        if(this.currentUserLanguage === 'es'){
          arrowImg = this.currentUserFieldColor === 'white' ? require(`@/assets/img/attack_spanish_dark.png`) : require(`@/assets/img/attack_spanish.png`);
        }else {
          arrowImg = this.currentUserFieldColor === 'white' ? require(`@/assets/img/attack_english_dark.png`) : require(`@/assets/img/attack_english.png`);
        }
        this.drawImageBase64(svg, arrowX,arrowY,arrowWidth,arrowHeight,arrowImg,`attackArrow`);

        if(this.mode === "lastDefenderDist"){
          this.renderLastDefender(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            tooltip);
        }else if(this.mode === "defensiveLineDist"){
          this.renderDefensiveLine(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            tooltip);
        }else if(this.mode === "fullBackAvgDist"){
          this.renderFullbackDistance(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            tooltip);
        }else if(this.mode === "offensiveLineDist"){
          this.renderOffensiveLine(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            tooltip);
        }

        let font = await loadFonts(this.fontFamily);
        copyAndAppendElement(svg.node(),font.node(),0,0);

        //Emit when is loaded
        this.$emit("loaded", this.id);
      },
      getPlayersWithHighestXG(shots) {
        // Group players by name
        const sumXGShots = shots.reduce(function(acc, val){
          var o = acc.filter(function(obj){
              return obj.player === val.player;
          }).pop() || {player: val.player, xG: 0};

          o.xG += val.xG;
          const index = acc.findIndex(p => p.player === o.player)
          if (index === -1)
            acc.push(o);
          else
            acc[index] = o;
          return acc;
        },[]);
        // Sort, take out players with xG < 0.1 and return first three
        return sumXGShots.sort((a, b) => b.xG - a.xG).filter(p => p.xG >= 0.1).slice(0, 3);
      },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        if(!this.images[idName]){
          let base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.src = img;
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(
            svg,
            x,
            y,
            width,
            height,
            dataUrl,
            idName,
            bottom
            );
          }
        }else{
          if(bottom){
            svg.node().insertBefore(this.images[idName],svg.node().firstChild);
          }else{
            svg.node().appendChild(this.images[idName]);
          }
        }
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("id", idName)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        return svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfShot(name) {
      	if (name === "SAVED SHOT") return "Saved Shot";
      	if (name === "GOAL") return "Goal";
      	if (name === "MISSED SHOT") return "Missed Shot";
      	if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "MISSED CHANCE") return "Chance Missed";
        if (name === "CHANCE MISSED") return "Chance Missed";
        if (name === "Regular play") return "Regular Play";
        if (name && name.startsWith("Individual")) return "Individual Play";
        if (name === "Fast break") return "Fast Break";
        if (name === "From corner") return "From Corner";
        if (name === "Free kick") return "Free Kick";
        if (name === "Set piece") return "Set Piece";
        if (name && name.startsWith("Throw")) return "Throw In";
        if (name === "Left footed") return "Left Foot";
        if (name === "Right footed") return "Right Foot";
        if (name === "Head") return "Head";
        if (name === "Other body part") return "Other Body Part";
        if (name === "Keeper Throw") return "Keeper Throw";
        if (name === "Penalty") return "Penalty";
        console.log("Unkown " + name);
      	return "??";
      },
      renderLastDefender(svg, pitchSize, pitchHeight, margin, elementId, tooltip){
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([pitchSize + margin.left, margin.left]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([pitchHeight + margin.top, margin.top]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        const simulation = d3v4
          .forceSimulation(this.data)
          .force("x", d3v4.forceX(d => x(100 - (d.avgLastDefenderDistanceOTip / 1.05))).strength(1))
          .force("y", d3v4.forceY(y(50)).strength(0.9))
          .force("collide", d3v4.forceCollide(radius(0.5)))
          .stop();

        _.forEach(this.data,(d)=> {
          if(!this.teamMap){
            simulation.tick();
          }else{
            d.x = x(100 - (d.avgLastDefenderDistanceOTip / 1.05));
            d.y = d.teamId === 99 ? y(45) : y(50);
          }
        });

        var self = this;
        svg.selectAll("circle")
          .data(this.data)
          .enter()
          .append("circle")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgLastDefenderDistanceOTip / 1.05)))
          .attr("cy", d => y(50))
          .attr('id', d => "circle-" + d.teamId + "-" + d.seasonId)
          .attr("class", d => "circle-" + d.teamId + "-" + d.seasonId)
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.distanceToGoal')}:</span>
                      <span style="padding-left:4px;"">${d.avgLastDefenderDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDistanceToGoal')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgLastDefenderDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().bottom + (circleRadius * 1.6);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                return (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2)) + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = x(100 - (d.avgLastDefenderDistanceOTip / 1.05)) - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgLastDefenderDistanceOTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            d3v4.select(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cy", (d) => {
            return d.y;
          })
      },
      renderDefensiveLine(svg, pitchSize, pitchHeight, margin, elementId, tooltip){
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([pitchSize + margin.left, margin.left]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([pitchHeight + margin.top, margin.top]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        const simulation = d3v4
          .forceSimulation(this.data)
          .force("x", d3v4.forceX(d => x(100 - (d.avgDefensiveLineDistanceOTip/1.05))).strength(1))
          .force("y", d3v4.forceY(d => y(50 + ((d.avgWidthDefensiveLineOTip/0.68)/2))).strength(1))
          .force("collide", d3v4.forceCollide(radius(0.3)))
          .stop();

        _.forEach(this.data,(d)=> {
          if(!this.teamMap){
            simulation.tick();
          }else{
            d.x = x(100 - (d.avgDefensiveLineDistanceOTip/1.05));
            d.y = y(50 + ((d.avgWidthDefensiveLineOTip/0.68)/2));
          }
        });

        var self = this;
        let teamGroup = svg.selectAll("circle")
          .data(this.data)
          .enter()
          .append("g");

        //topCircles
        let topCircles = teamGroup  
          .append("circle")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgDefensiveLineDistanceOTip/1.05)))
          .attr("cy", d => y(50 + ((d.avgWidthDefensiveLineOTip/0.68)/2)))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-top`)
          .attr("class", d => `circle-${d.teamId}-${d.seasonId}`)
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.defensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().top - (circleRadius * 13);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            //LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDefensiveLineDistanceOTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            
            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId );

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthDefensiveLineOTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => d.x)
          .attr("cy", d => d.y);          

        //bottomCircles
        let bottomCircles = teamGroup  
          .append("circle")
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgDefensiveLineDistanceOTip / 1.05)))
          .attr("cy", d => y(50 - ((d.avgWidthDefensiveLineOTip / 0.68)/2)))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-bottom`)
          .attr("class", d =>`circle-${d.teamId}-${d.seasonId}`)
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.defensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().bottom + (circleRadius * 1.6);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            // LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDefensiveLineDistanceOTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            
            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthDefensiveLineOTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("cy", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return pitchHeight - tC.__data__.y
          })

          //linesBetweenCircles
          let linesBetweenCircles = teamGroup  
          .append("line")
          .attr("x1", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("y1", d => y(50))
          .attr("x2", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("y2", d => y(50))
          .attr("stroke-width", 1)
          .attr('id', d => `between-line-${d.teamId}-${d.seasonId}`)
          .attr("class", d => "between-line")
          .style("opacity", 1)
          .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.defensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDefensiveLineDistanceOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthDefensiveLineOTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().bottom + (circleRadius * 1.6);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            //LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDefensiveLineDistanceOTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthDefensiveLineOTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("stroke-width", 2)
          .attr("y1", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.y + tC.r.baseVal.value
          })
          .attr("y2", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return pitchHeight - tC.__data__.y - tC.r.baseVal.value
          })

          d3v4.selectAll(".between-line").lower();

      },
      renderFullbackDistance(svg, pitchSize, pitchHeight, margin, elementId, tooltip){
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([pitchSize + margin.left, margin.left]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([pitchHeight + margin.top, margin.top]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        const simulation = d3v4
          .forceSimulation(this.data)
          .force("x", d3v4.forceX(d => x(100 - (d.avgLbDistanceTip / 1.05))).strength(1))
          .force("y", d3v4.forceY(d => y((d.avgTipYLB))).strength(0.5))
          .force("collide", d3v4.forceCollide(radius(0.3)))
          .stop();

        _.forEach(this.data,(d)=> {
          if(!this.teamMap){
            simulation.tick();
          }else{
            d.x = x(100 - (d.avgLbDistanceTip / 1.05));
            d.y = y(d.avgTipYLB);
          }
        });

        var self = this;
        let teamGroup = svg.selectAll("circle")
          .data(this.data)
          .enter()
          .append("g");

        //topCircles
        let topCircles = teamGroup  
          .append("circle")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgLbDistanceTip / 1.05)))
          .attr("cy", d => y((d.avgTipYLB)))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-top`)
          .attr("class", d => `circle-${d.teamId}-${d.seasonId}`)
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            let bC = _.find(bottomCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
                        
            var currentNode =  d3.select(this).node();
            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            
            //TOOLTIP TOP - START
            let tooltipTopHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span><span style="margin-left:8px;">LB</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.distance')}:</span>
                      <span style="padding-left:4px;"">${d.avgLbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgPositionYAxis')}:</span>
                      <span style="padding-left:4px;">${(d.avgTipYLB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgLbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgPositionYAxis')}:</span>
                      <span style="padding-left:4px;">${(self.averageDataBySeason[d.seasonId].avgTipYLB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `

            var tooltipTopY = tC.getBoundingClientRect().top - (circleRadius * 11);
            let tooltipTopX = tC.getBoundingClientRect().left + (circleRadius * 4);

            d3.select("body > #top.tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipTopHTML)
              .style("left",function(e) {
                let left = tooltipTopX;
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTopY) + "px")
            //END - TOOLTIP TOP

            //TOOLTIP BOTTOM - START
            // let tooltipBottomHTML = `
            //   <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
            //     <div style="display:flex;align-items:center;">
            //       <img
            //       src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
            //       style="height: 22px; width: 22px;margin-right:4px;"
            //       />
            //       <span style="font-weight:bold">${d.teamName}</span><span style="margin-left:8px;">RB</span>
            //     </div>

            //     <div style="display:flex;">
            //       <div style="display:flex;flex-direction:column;">
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.distance')}:</span>
            //           <span style="padding-left:4px;"">${d.avgRbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
            //           <span style="padding-left:4px;">${(d.avgTipYLB - d.avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //       </div>
            //       <div style="display:flex;flex-direction:column;margin-left:8px;">
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
            //           <span style="padding-left:4px;">${self.averageData.avgRbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
            //           <span style="padding-left:4px;">${(self.averageData.avgTipYLB - self.averageData.avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //       </div>
            //     </div>
            //   </div>
            // `

            // var tooltipBottomY = bC.getBoundingClientRect().top - (circleRadius * 11);
            // let tooltipBottomX = currentNode.getBoundingClientRect().left + (circleRadius * 4);

            // d3.select("body > #bottom.tooltip-no-arrow")
            // .attr("font-weight", 500)
            //   .style("font-size", "14px")
            //   .style("display", "inline-block")
            //   .style("position", "fixed")
            //   .html(tooltipBottomHTML)
            //   .style("left",function(e) {
            //     let left = tooltipBottomX;
            //     let fieldLeft = svg.node().getBoundingClientRect().left;
            //     if(left < fieldLeft){
            //       left = fieldLeft + 10;
            //     }
            //     return left + "px"
            //   }) 
            //   .style("top", (tooltipBottomY) + "px")
            // END - TOOLTIPBOTTOM

            //LINE TO GOAL - START
            let sx0 = x(100);
            let sy0 = tC.cy.baseVal.value;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = sy0;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0 - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", sy0 + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", sy0 - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", sy0 + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", sy0 - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgLbDistanceTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            //END - LINE TO GOAL
            
            //LINE BETWEEN CIRCLES - START
            let sx0V = _.max([tC.__data__.x,bC.cx.baseVal.value]) + radius(1.2);
            let sy0V = tC.cy.baseVal.value;
            let sx1V = sx0V;
            let sy1V = bC.cy.baseVal.value;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${(d.avgTipYLB - d.avgTipYRB).toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();
            //END - LINE BETWEEN CIRCLES

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.selectAll("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => d.x)
          .attr("cy", d => d.y)


        //bottomCircles
        let bottomCircles = teamGroup  
          .append("circle")
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgRbDistanceTip / 1.05)))
          .attr("cy", d => y(d.avgTipYRB))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-bottom`)
          .attr("class", d => `circle-${d.teamId}-${d.seasonId}`)
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            let bC = _.find(bottomCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
                        
            var currentNode =  d3.select(this).node();
            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            
            //TOOLTIP TOP - START
            // let tooltipTopHTML = `
            //   <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
            //     <div style="display:flex;align-items:center;">
            //       <img
            //       src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
            //       style="height: 22px; width: 22px;margin-right:4px;"
            //       />
            //       <span style="font-weight:bold">${d.teamName}</span><span style="margin-left:8px;">LB</span>
            //     </div>

            //     <div style="display:flex;">
            //       <div style="display:flex;flex-direction:column;">
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.distance')}:</span>
            //           <span style="padding-left:4px;"">${d.avgLbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
            //           <span style="padding-left:4px;">${(d.avgTipYLB - d.avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //       </div>
            //       <div style="display:flex;flex-direction:column;margin-left:8px;">
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
            //           <span style="padding-left:4px;">${self.averageData.avgLbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //         <div style="display:flex;">
            //           <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
            //           <span style="padding-left:4px;">${(self.averageData.avgTipYLB - self.averageData.avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
            //         </div>
            //       </div>
            //     </div>
            //   </div>
            // `

            // var tooltipTopY = tC.getBoundingClientRect().top - (circleRadius * 11);
            // let tooltipTopX = tC.getBoundingClientRect().left + (circleRadius * 4);

            // d3.select("body > #top.tooltip-no-arrow")
            // .attr("font-weight", 500)
            //   .style("font-size", "14px")
            //   .style("display", "inline-block")
            //   .style("position", "fixed")
            //   .html(tooltipTopHTML)
            //   .style("left",function(e) {
            //     let left = tooltipTopX;
            //     let fieldLeft = svg.node().getBoundingClientRect().left;
            //     if(left < fieldLeft){
            //       left = fieldLeft + 10;
            //     }
            //     return left + "px"
            //   }) 
            //   .style("top", (tooltipTopY) + "px")
            //END - TOOLTIP TOP

            //TOOLTIP BOTTOM - START
            let tooltipBottomHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span><span style="margin-left:8px;">RB</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.distance')}:</span>
                      <span style="padding-left:4px;"">${d.avgRbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgPositionYAxis')}:</span>
                      <span style="padding-left:4px;">${(d.avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgRbDistanceTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgPositionYAxis')}:</span>
                      <span style="padding-left:4px;">${(self.averageDataBySeason[d.seasonId].avgTipYRB).toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `

            var tooltipBottomY = bC.getBoundingClientRect().top - (circleRadius * 11);
            let tooltipBottomX = currentNode.getBoundingClientRect().left + (circleRadius * 4);

            d3.select("body > #bottom.tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipBottomHTML)
              .style("left",function(e) {
                let left = tooltipBottomX;
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipBottomY) + "px")
            // END - TOOLTIPBOTTOM

            //LINE TO GOAL - START
            let sx0 = x(100);
            let sy0 = bC.cy.baseVal.value;
            let sx1 = bC.cx.baseVal.value - (radius(0.5) * 1.3);
            let sy1 = sy0;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0 - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", sy0 + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", sy0 - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", sy0 + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", sy0 - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgRbDistanceTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            //END - LINE TO GOAL
            
            //LINE BETWEEN CIRCLES - START
            let sx0V = _.max([tC.__data__.x,bC.cx.baseVal.value]) + radius(1.2);
            let sy0V = tC.cy.baseVal.value;
            let sx1V = sx0V;
            let sy1V = bC.cy.baseVal.value;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${(d.avgTipYLB - d.avgTipYRB).toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();
            //END - LINE BETWEEN CIRCLES

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.selectAll("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            let base = tC.__data__.x;
            let diff = x(0) - x(Math.abs(d.avgRbDistanceTip - d.avgLbDistanceTip))
            let adjusted = d.avgRbDistanceTip >= d.avgLbDistanceTip ? base + diff : base - diff;
            return adjusted;
          })
          .attr("cy", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return  pitchHeight - tC.__data__.y;
          })
      },
      renderOffensiveLine(svg, pitchSize, pitchHeight, margin, elementId, tooltip){
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([pitchSize + margin.left, margin.left]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([pitchHeight + margin.top, margin.top]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        const simulation = d3v4
          .forceSimulation(this.data)
          .force("x", d3v4.forceX(d => x(100 - (d.avgDistanceOffensiveLineTip/ 1.05))).strength(1))
          .force("y", d3v4.forceY(d => y(50 + ((d.avgWidthOffensiveLineTip / 0.68)/2))).strength(0.8))
          .force("collide", d3v4.forceCollide(radius(0.3)))
          .stop();

        _.forEach(this.data,(d)=> {
          if(!this.teamMap){
            simulation.tick();
          }else{
            d.x = x(100 - (d.avgDistanceOffensiveLineTip/ 1.05));
            d.y = y(50 + ((d.avgWidthOffensiveLineTip / 0.68)/2));
          }
        });

        var self = this;
        let teamGroup = svg.selectAll("circle")
          .data(this.data)
          .enter()
          .append("g");

        //topCircles
        let topCircles = teamGroup  
          .append("circle")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(100 - (d.avgDistanceOffensiveLineTip/ 1.05)))
          .attr("cy", d => y(50 + ((d.avgWidthOffensiveLineTip / 0.68)/2)))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-top`)
          .attr("class", d => `circle-${d.teamId}-${d.seasonId}`)
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');

            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.offensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().top - (circleRadius * 13);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            //LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDistanceOffensiveLineTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            
            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthOffensiveLineTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => d.x)
          .attr("cy", d => d.y)


        //bottomCircles
        let bottomCircles = teamGroup  
          .append("circle")
          .style("fill-opacity", (d) => 0.90)
          .style("opacity", 0.95)
          .style("stroke", d => "Gainsboro")
          .style("stroke-width", pitchSize * 0.001)
          .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver")
          .attr("r", d => radius(0.1))
          .attr("cx", d => x(33))
          .attr("cy", d => y(50 - ((d.avgWidthOffensiveLineTip / 0.68)/2)))
          .attr('id', d => `circle-${d.teamId}-${d.seasonId}-bottom`)
          .attr("class", d => `circle-${d.teamId}-${d.seasonId}`)
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.offensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().bottom + (circleRadius * 1.6);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            // LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDistanceOffensiveLineTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");
            
            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthOffensiveLineTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("cx", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("cy", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return pitchHeight - tC.__data__.y
          })

          //linesBetweenCircles
          let linesBetweenCircles = teamGroup  
          .append("line")
          .attr("x1", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("y1", d => y(50))
          .attr("x2", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.x
          })
          .attr("y2", d => y(50))
          .attr("stroke-width", 1)
          .attr('id', d => `between-line-${d.teamId}-${d.seasonId}`)
          .attr("class", d => `between-line`)
          .style("opacity", 1)
          .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver")
          .on('mouseenter',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius(0.5))
              .style("fill", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .style("cursor", "pointer")
              .attr('stroke-width', 4)
              .attr("stroke", d => d.teamColor ? d.teamColor.replace('0x','#') : 'black');
            
            let tooltipHTML = `
              <div style="display:flex;flex-direction:column;align-items:start;padding: 6px;font-size:12px;">
                <div style="display:flex;align-items:center;">
                  <img
                  src="https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${d.teamId != 99 ? d.teamId : 0}.png"
                  style="height: 22px; width: 22px;margin-right:4px;"
                  />
                  <span style="font-weight:bold">${d.teamName}</span>
                  <span style="padding-left:2px">· ${d.leagueLabel} ${d.year}</span>
                </div>

                <div style="display:flex;">
                  <div style="display:flex;flex-direction:column;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.offensiveLineDist')}:</span>
                      <span style="padding-left:4px;"">${d.avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.avgLineWidth')}:</span>
                      <span style="padding-left:4px;">${d.avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                  <div style="display:flex;flex-direction:column;margin-left:8px;">
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgDist')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgDistanceOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                    <div style="display:flex;">
                      <span>${self.$t('spatialCoverage.leagueAvgLineWidth')}:</span>
                      <span style="padding-left:4px;">${self.averageDataBySeason[d.seasonId].avgWidthOffensiveLineTip.toFixed(1)} ${self.$t('spatialCoverage.meters')}</span>
                    </div>
                  </div>
                </div>
              </div>
            `
            
            var currentNode =  d3.select(this).node();

            var circleRadius = Math.ceil(d3.select(this).attr('r'));
            var tooltipTop = currentNode.getBoundingClientRect().bottom + (circleRadius * 1.6);
            let tooltipLeft = currentNode.getBoundingClientRect().left;

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("font-size", "14px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html(tooltipHTML)
              .style("left",function(e) {
                let left = (tooltipLeft - (d3.select(this).node().getBoundingClientRect().width/2));
                let fieldLeft = svg.node().getBoundingClientRect().left;
                if(left < fieldLeft){
                  left = fieldLeft + 10;
                }
                return left + "px"
              }) 
              .style("top", (tooltipTop) + "px")

            //LINE TO GOAL
            let sx0 = x(100);
            let sy0 = d.y;
            let sx1 = d.x - (radius(0.5) * 1.3);
            let sy1 = d.y;
            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx0)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1)
            .attr("y1", d.y - radius(0.15))
            .attr("x2", sx1)
            .attr("y2", d.y + radius(0.15))
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroup = svg.append("g");

            let textEl = textGroup.append("text")
            .attr("x", (sx1 - ((sx1-sx0) / 2)))
            .attr("y", d.y - radius(0.15))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgDistanceOffensiveLineTip.toFixed(1)}m`);

            textGroup.append("rect")
            .attr("x", (textEl.node().getBBox().x) - 4)
            .attr("y", (textEl.node().getBBox().y) - 2)
            .attr("width",(textEl.node().getBBox().width) + 8)
            .attr("height",(textEl.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            //LINE BETWEEN CIRCLES
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);

            let sx0V = tC.__data__.x + radius(1.2);
            let sy0V = tC.__data__.y;
            let sx1V = tC.__data__.x + radius(1.2);
            let sy1V = pitchHeight - tC.__data__.y;
            svg.append("line")
            .attr("x1", sx0V)
            .attr("y1", sy0V)
            .attr("x2", sx1V)
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx0V - radius(0.15))
            .attr("y1", sy0V)
            .attr("x2", sx0V + radius(0.15))
            .attr("y2", sy0V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            svg.append("line")
            .attr("x1", sx1V - radius(0.15))
            .attr("y1", sy1V)
            .attr("x2", sx1V + radius(0.15))
            .attr("y2", sy1V)
            .attr("stroke-width", 2)
            .attr("class", "distance-line")
            .style("opacity", 1)
            .attr("stroke", "black");

            let textGroupV = svg.append("g");

            let textElV = textGroupV.append("text")
            .attr("x", sx1V + radius(1))
            .attr("y", (sy1V - ((sy1V-sy0V) / 2)))
            .attr("text-anchor", "middle")
            .attr("font-weight", 500)
            .style("font-size", "12px")
            .style("fill", "white")
            .attr("class","text-distance")
            .text(`${d.avgWidthOffensiveLineTip.toFixed(1)}m`);

            textGroupV.append("rect")
            .attr("x", (textElV.node().getBBox().x) - 4)
            .attr("y", (textElV.node().getBBox().y) - 2)
            .attr("width",(textElV.node().getBBox().width) + 8)
            .attr("height",(textElV.node().getBBox().height) + 4)
            .style("fill", "black")
            .attr("class","box-text-distance");

            d3v4.selectAll(".box-text-distance").lower();

          })
          .on('mouseout',function(d) {
            d3.selectAll(`.circle-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(200)
              .attr('r', d => radius(0.1))
              .style("fill", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select(`#between-line-${d.teamId}-${d.seasonId}`)
              .transition()
              .duration(100)
              .style("cursor", "pointer")
              .attr('stroke-width', 3)
              .attr("stroke", d => d.teamId === 99 ? "DimGray" : "Silver");
            d3.select("body > .tooltip-no-arrow").style("display", "none");
            svg.selectAll(".distance-line").remove();
            svg.selectAll(".text-distance").remove();
            svg.selectAll(".box-text-distance").remove();
          })
          .transition()
          .duration(450)
          .attr("stroke-width", 2)
          .attr("y1", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return tC.__data__.y + tC.r.baseVal.value
          })
          .attr("y2", d => {
            let tC = _.find(topCircles[0],(c) => c.__data__.teamId == d.teamId && c.__data__.seasonId == d.seasonId);
            return pitchHeight - tC.__data__.y - tC.r.baseVal.value
          })

          d3v4.selectAll(".between-line").lower();

      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
      d3.select(`#${this.rootElementId}`).select("svg").remove();
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/SpatialCoverageChart.vue